import { Inject, Injectable } from "@angular/core";
import { CircuitService } from "./circuit.service";
import { EndsData } from "../wizard/steps/1-ends/end-step-circuit-dwdm.model";
import { NmsWizardStepValidationResponse } from "@nms-angular-toolkit/nms-wizard";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { ErrorResponseHandlerService } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.service";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { TranspondersData } from "../wizard/steps/2-transponders/transponders-step.model";
import { ANGULARJS_TRANSLATE, LOADING_PANE_SERVICE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { ErrorResponse, NmsError } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.model";
import { ERROR_NAME } from "@nms-ng2/app/core/services/error-response-handler/error-names.constant";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";
import { IntermediateDevicesValidatorData } from "../wizard/steps/3-intermediates/intermediates-step.model";
import { LinksValidatorData } from "../wizard/steps/4-links/links-step.model";
import { AmplifiersValidatorData } from "../wizard/steps/5-amplifiers/amplifiers-step.model";
import { OscsValidatorData } from "../wizard/steps/6-osc-management/osc-management-step.model";

/**
 * Serviço responsável pela validação referente aos circuitos DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class CircuitValidationService extends CircuitService {

    constructor(http: HttpClient, errorResponseHandlerService: ErrorResponseHandlerService, nmsDialogService: NmsDialogService,
            @Inject(LOADING_PANE_SERVICE) paneService: any, @Inject(ANGULARJS_TRANSLATE) translate: any,
            private readonly translationHelper: TranslationHelperService) {
        super(http, errorResponseHandlerService, nmsDialogService, paneService, translate);
    }

    async validateEnds(ends: EndsData): Promise<NmsWizardStepValidationResponse> {
        try {
            await this.http.post<void>(`${this.baseUrl}/validation/ends`, ends).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch(error) {
            return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
        }
    }

    async validateTransponders(transponders: TranspondersData): Promise<NmsWizardStepValidationResponse> {
        try {
            await this.http.post<void>(`${this.baseUrl}/validation/transponders`, transponders).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch(error) {
            const { code } = error.error;
            switch(code) {
                case NmsError.DWDM_CIRCUIT_CHANNEL_USED_BY_MUX_ERROR:
                case NmsError.DWDM_CIRCUIT_INVALID_MUX_CHANNEL_RANGE:
                case NmsError.DWDM_CIRCUIT_CHANNEL_IN_USE_BY_PORT_ERROR:
                    const message = this.buildMessageWithMultipleDetailsParameters(error.error);
                    return Promise.reject(new NmsWizardStepValidationResponse(false, message));
                default:
                    return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
            }
        }
    }

    async validateIntermediates(intermediates: IntermediateDevicesValidatorData) {
        try {
            await this.http.post<void>(`${this.baseUrl}/validation/intermediates`, intermediates).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch(error) {
            const { code } = error.error;

            if (code === NmsError.DWDM_CIRCUIT_INTERMEDIATE_WITHOUT_EDFA_PORT) {
                error.error.details = [error.error.details.join("<br/>")];
                const message = this.buildMessageWithMultipleDetailsParameters(error.error);
                return Promise.reject(new NmsWizardStepValidationResponse(false, message));
            }

            return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
        }
    }

    async validateLinks(links: LinksValidatorData) {
        try {
            await this.http.post<void>(`${this.baseUrl}/validation/links`, links).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch (error) {
            const { code } = error.error;
            switch(code) {
                case NmsError.DWDM_CIRCUIT_LINK_NOT_ACTIVE_SITUATION:
                case NmsError.DWDM_CIRCUIT_LINK_TYPE_IS_NOT_DWDM:
                case NmsError.DWDM_CIRCUIT_LINK_MUX_CANNOT_BE_USED:
                case NmsError.DWDM_CIRCUIT_LINK_DEVICES_NOT_IN_CIRCUIT:
                case NmsError.DWDM_CIRCUIT_LINK_TX_RX_REQUIRED:
                case NmsError.DWDM_CIRCUIT_LINK_FOUR_HUNDRED_CANNOT_BE_USED:
                case NmsError.DWDM_CIRCUIT_LINKS_WITH_MUX_NOT_ALLOWED:
                case NmsError.DWDM_CIRCUIT_INTERMEDIATES_MUST_HAVE_EDFA_PORT:
                    error.error.details = [error.error.details.join("<br/>")];
                    const message = this.buildMessageWithMultipleDetailsParameters(error.error);
                    return Promise.reject(new NmsWizardStepValidationResponse(false, message));
                default:
                    return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
            }
        }
    }

    async validateAmplifiers(amplifiersValidatorData: AmplifiersValidatorData) {
        try {
            await this.http.post<void>(`${this.baseUrl}/validation/amplifiers`, amplifiersValidatorData).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch (error) {
            return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
        }
    }

    async validateOscs(oscsValidatorData: OscsValidatorData) {
        try {
            await this.http.post<void>(`${this.baseUrl}/validation/oscs`, oscsValidatorData).toPromise();
            return Promise.resolve(new NmsWizardStepValidationResponse(true));
        } catch (error) {
            const { code } = error.error;
            switch(code) {
                case NmsError.DWDM_CIRCUIT_ENDS_TX_MUST_HAVE_A_TX_OSC:
                case NmsError.DWDM_CIRCUIT_ENDS_RX_MUST_HAVE_A_RX_OSC:
                case NmsError.DWDM_CIRCUIT_MULTIPLE_OSC_MANAGEMENT_PROFILES:
                    error.error.details = [error.error.details.join("<br/>")];
                    const message = this.buildMessageWithMultipleDetailsParameters(error.error);
                    return Promise.reject(new NmsWizardStepValidationResponse(false, message));
                default:
                    return Promise.reject(new NmsWizardStepValidationResponse(false, this.buildErrorMessage(error)));
            }
        }
    }

    /**
     * Mensagem customizada para os errorCodes
     * DWDM_CIRCUIT_CHANNEL_USED_BY_MUX_ERROR
     * DWDM_CIRCUIT_INVALID_MUX_CHANNEL_RANGE
     * DWDM_CIRCUIT_CHANNEL_IN_USE_BY_PORT_ERROR
     * DWDM_CIRCUIT_INTERMEDIATE_WITHOUT_EDFA_PORT
     * DWDM_CIRCUIT_LINK_FOUR_HUNDRED_CANNOT_BE_USED
     * DWDM_CIRCUIT_LINKS_WITH_MUX_NOT_ALLOWED
     * DWDM_CIRCUIT_INTERMEDIATES_MUST_HAVE_EDFA_PORT
     * no qual necessita de parâmetros que vêm detalhados na exceção de acordo com
     * o erro ocorrido.
     * @param errorResponse detalhes do erro ocorrido
     * @returns mensagem formatada com os detalhes preenchidos
     */
    private buildMessageWithMultipleDetailsParameters(errorResponse: ErrorResponse): string {
        const { code, details } = errorResponse;
        const errorName = ERROR_NAME[code];

        if (!errorName) {
            const unknownError = this.translationHelper.translateByPrefix("response.error.", "UNEXPECTED_ERROR");
            throw new Error(`${unknownError} ${code}`);
        }

        return this.translationHelper.translateWithReplacement(`response.error.${errorName}`, ...details);
    }

    private buildErrorMessage(error: HttpErrorResponse, message = "") {
        const { description, details } = this.errorResponseHandlerService.buildErrorDescriptionDetails(error.error);

        return `${message}${description}${details}`;
    }
}
